'use client';

import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { SPACING } from '@/constants/spacing';
import { TYPOGRAPHY, FONT_SIZE } from '@/constants/typography';
import { BREAKPOINTS } from '@/constants/breakpoints';

// Shared form primitives, extracted from app/contact-us/ContactUsPage.tsx so
// every form on the site (contact, blog lead popup, blog comments) looks and
// behaves the same instead of each one redefining its own inputs.

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const FormField = styled.div`
  margin-bottom: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    margin-bottom: ${SPACING.sm};
  }
`;

export const Label = styled.label`
  ${TYPOGRAPHY.bodySM}
  font-weight: 600;
  color: ${COLORS.textPrimary};
  display: block;
  margin-bottom: ${SPACING.xs};
`;

export const Required = styled.span`
  color: ${COLORS.error};
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  transition: all 0.2s ease;

  &::placeholder {
    color: ${COLORS.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
  }

  &:disabled {
    background: ${COLORS.bgGray};
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;
  font-family: inherit;

  &::placeholder {
    color: ${COLORS.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
  }

  &:disabled {
    background: ${COLORS.bgGray};
    cursor: not-allowed;
  }
`;

export const Select = styled.select<{ $hasError?: boolean }>`
  ${TYPOGRAPHY.body}
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid ${(props) => (props.$hasError ? COLORS.error : COLORS.borderGray)};
  border-radius: 6px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  transition: all 0.2s ease;
  appearance: auto;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? COLORS.error : COLORS.brandGreen)};
  }

  &:disabled {
    background: ${COLORS.bgGray};
    cursor: not-allowed;
    color: ${COLORS.textMuted};
  }
`;

export const FieldError = styled.div`
  ${TYPOGRAPHY.bodyXS}
  color: ${COLORS.error};
  margin-top: ${SPACING.xs};
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '⚠';
  }
`;

export const SubmitButton = styled.button`
  ${TYPOGRAPHY.button}
  width: 100%;
  padding: ${SPACING.md} ${SPACING.xl};
  background: linear-gradient(135deg, ${COLORS.brandGreen} 0%, ${COLORS.successAlt} 100%);
  color: ${COLORS.white};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${COLORS.successAlt} 0%, ${COLORS.brandGreen} 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${COLORS.gray600};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const StatusMessage = styled.div<{ $variant: 'success' | 'error' }>`
  ${TYPOGRAPHY.bodySM}
  background: ${(props) => (props.$variant === 'success' ? COLORS.success : COLORS.error)};
  color: ${COLORS.white};
  padding: ${SPACING.sm} ${SPACING.md};
  border-radius: 8px;
  margin-bottom: ${SPACING.md};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: ${FONT_SIZE.body.xs};
  }
`;
